import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Card, PrimaryButton } from "../components/common";
import { toast } from "sonner";
import { ShareNetwork, Copy, ArrowClockwise, ArrowSquareOut, Image as ImageIcon } from "@phosphor-icons/react";
import { fileUrl } from "../lib/api";

export default function PortalCliente() {
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [brandTitle, setBrandTitle] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [savingBrand, setSavingBrand] = useState(false);
  const url = share ? `${window.location.origin}/obra/${share.token}` : "";

  const load = () => api.get("/client-share").then((r) => { setShare(r.data); setBrandTitle(r.data.portal_title || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveBranding = async () => {
    setSavingBrand(true);
    const fd = new FormData();
    fd.append("title", brandTitle);
    if (logoFile) fd.append("file", logoFile);
    try {
      const r = await api.put("/client-share/branding", fd);
      setShare((s) => ({ ...s, portal_title: r.data.portal_title, portal_logo: r.data.portal_logo }));
      setLogoFile(null); toast.success("Personalización guardada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingBrand(false); }
  };

  const toggle = async (enabled) => {
    const fd = new FormData(); fd.append("enabled", enabled ? "true" : "false");
    try { const r = await api.put("/client-share/toggle", fd); setShare((s) => ({ ...s, enabled: r.data.enabled })); toast.success(enabled ? "Portal activado" : "Portal desactivado"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const regenerate = async () => {
    if (!window.confirm("Al regenerar, el enlace anterior dejará de funcionar. ¿Continuar?")) return;
    setBusy(true);
    try { const r = await api.post("/client-share/regenerate"); setShare(r.data); toast.success("Nuevo enlace generado"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(url); toast.success("Enlace copiado"); };

  return (
    <div>
      <PageHeader overline="Compartir avance" title="Portal del cliente" />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Comparte un enlace <b className="text-foreground">público de solo lectura</b> con el dueño de la obra. Muestra el
        avance general, los frentes de trabajo y las fotografías — <b className="text-foreground">sin información financiera</b>.
      </Card>

      {share && (
        <Card className="p-6 max-w-2xl" data-testid="portal-admin">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ShareNetwork size={20} weight="fill" className="text-primary" />
              <span className="font-semibold">Enlace del portal</span>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <span className={share.enabled ? "text-success font-semibold" : "text-muted-foreground"}>{share.enabled ? "Activo" : "Inactivo"}</span>
              <input type="checkbox" data-testid="portal-toggle" checked={!!share.enabled} onChange={(e) => toggle(e.target.checked)} className="w-4 h-4 accent-primary" />
            </label>
          </div>

          <div className="flex items-stretch gap-2">
            <input readOnly value={url} data-testid="portal-url" className="flex-1 h-11 px-3 rounded-sm border border-input bg-muted/40 text-sm font-mono" />
            <button onClick={copy} data-testid="portal-copy" className="inline-flex items-center gap-1.5 h-11 px-4 border border-input rounded-sm text-sm font-medium hover:bg-muted transition-colors duration-200"><Copy size={16} weight="bold" /> Copiar</button>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <PrimaryButton onClick={() => window.open(url, "_blank")} data-testid="portal-open"><ArrowSquareOut size={16} weight="bold" /> Abrir portal</PrimaryButton>
            <button onClick={regenerate} disabled={busy} data-testid="portal-regenerate" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-danger transition-colors duration-200">
              <ArrowClockwise size={15} weight="bold" className={busy ? "animate-spin" : ""} /> Regenerar enlace
            </button>
          </div>
        </Card>
      )}

      {share && (
        <Card className="p-6 max-w-2xl mt-6" data-testid="portal-branding">
          <div className="flex items-center gap-2 mb-5">
            <ImageIcon size={20} weight="fill" className="text-primary" />
            <span className="font-semibold">Personalización (título y logo)</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 items-end">
            <div>
              <label className="overline block mb-1">Nombre de la obra</label>
              <input data-testid="brand-title" className="w-full h-11 px-3 rounded-sm border border-input text-sm" placeholder="Ej. Plaza Tarantella" value={brandTitle} onChange={(e) => setBrandTitle(e.target.value)} />
            </div>
            <div>
              <label className="overline block mb-1">Logo (PNG/JPG)</label>
              <input type="file" accept="image/*" data-testid="brand-logo" className="w-full text-sm py-2" onChange={(e) => setLogoFile(e.target.files[0])} />
            </div>
          </div>
          {share.portal_logo && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Logo actual:</span>
              <img src={fileUrl(share.portal_logo)} alt="logo actual" className="h-8 w-auto object-contain bg-slate-900 rounded-sm px-2 py-1" />
            </div>
          )}
          <div className="mt-5">
            <PrimaryButton onClick={saveBranding} disabled={savingBrand} data-testid="save-brand-btn">{savingBrand ? "Guardando…" : "Guardar personalización"}</PrimaryButton>
          </div>
        </Card>
      )}
    </div>
  );
}
